import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserCircle2, MapPin, KeyRound, Plus, Trash2, Star, Save } from "lucide-react";

const emptyAddr = { label: "Home", name: "", phone: "", line1: "", line2: "", landmark: "", city: "", state: "", pincode: "", is_default: false };

export default function CustomerProfile() {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [addrDialog, setAddrDialog] = useState(false);
  const [addrForm, setAddrForm] = useState(emptyAddr);
  const [addrBusy, setAddrBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const load = () => api.get("/addresses").then((r) => setAddresses(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setAddrBusy(true);
    try { await api.post("/addresses", addrForm); toast.success("Address added"); setAddrDialog(false); setAddrForm(emptyAddr); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setAddrBusy(false); }
  };
  const del = async (a) => {
    if (!window.confirm(`Delete "${a.label} · ${a.name}"?`)) return;
    try { await api.delete(`/addresses/${a.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const makeDefault = async (a) => {
    try { await api.post(`/addresses/${a.id}/default`); toast.success("Default set"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const changePw = async () => {
    if (!pw.current || pw.next.length < 8) { toast.error("New password must be 8+ chars"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "" });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPwBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 grid lg:grid-cols-[280px_1fr] gap-8" data-testid="customer-profile-page">
      {/* Sidebar */}
      <aside className="space-y-2">
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-secondary/40 border">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent to-primary text-white grid place-items-center text-lg font-semibold">
            {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{user?.name || "Customer"}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            <Badge className="mt-1 text-[10px]" variant="secondary">{user?.role}</Badge>
          </div>
        </div>
        <nav className="space-y-1 text-sm">
          <Link to="/account/orders" className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary" data-testid="link-my-orders">
            <UserCircle2 className="w-4 h-4" /> My orders
          </Link>
        </nav>
      </aside>

      <main className="space-y-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold">My account</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage addresses, password and preferences.</p>
        </div>

        {/* Addresses */}
        <Card className="card-soft border-transparent" data-testid="addresses-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-accent" /><div className="font-display text-lg">Saved addresses</div></div>
              <Button size="sm" onClick={() => { setAddrForm(emptyAddr); setAddrDialog(true); }} data-testid="profile-add-address-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
            </div>

            {addresses.length === 0 && (
              <div className="rounded-xl border border-dashed p-6 text-center text-muted-foreground text-sm">
                You haven&rsquo;t saved any addresses yet.
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              {addresses.map((a) => (
                <div key={a.id} className={`rounded-xl border p-3 text-sm relative ${a.is_default ? "border-accent bg-accent/5" : ""}`} data-testid={`profile-addr-${a.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-semibold">{a.name}</div>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{a.label || "home"}</span>
                    {a.is_default && <Badge className="bg-accent hover:bg-accent">Default</Badge>}
                  </div>
                  <div className="text-muted-foreground text-xs">{a.phone}</div>
                  <div className="text-muted-foreground text-xs">{[a.line1, a.line2, a.landmark].filter(Boolean).join(", ")}</div>
                  <div className="text-muted-foreground text-xs">{a.city}, {a.state} — {a.pincode}</div>
                  <div className="flex items-center gap-1 mt-2">
                    {!a.is_default && <Button variant="ghost" size="sm" onClick={() => makeDefault(a)} data-testid={`set-default-${a.id}`}><Star className="w-3 h-3 mr-1" />Set default</Button>}
                    <Button variant="ghost" size="sm" onClick={() => del(a)} data-testid={`del-addr-${a.id}`}><Trash2 className="w-3 h-3 mr-1 text-destructive" />Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Password */}
        <Card className="card-soft border-transparent" data-testid="password-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4"><KeyRound className="w-4 h-4 text-accent" /><div className="font-display text-lg">Change password</div></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Current password</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="cust-pw-current" /></div>
              <div><Label>New password</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="8+ chars" data-testid="cust-pw-next" /></div>
            </div>
            <Button onClick={changePw} disabled={pwBusy} className="mt-4 btn-primary-glow" data-testid="cust-save-pw-btn"><Save className="w-4 h-4 mr-1" />Update password</Button>
          </CardContent>
        </Card>

        {/* Account info */}
        <Card className="card-soft border-transparent">
          <CardContent className="p-5 text-sm space-y-1">
            <div className="font-display text-lg mb-2">Account details</div>
            <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span>{user?.name || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span>{user?.phone || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Joined</span><span>{fmtDate(user?.created_at)}</span></div>
          </CardContent>
        </Card>
      </main>

      <Dialog open={addrDialog} onOpenChange={setAddrDialog}>
        <DialogContent data-testid="profile-address-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">New address</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Label</Label><Input value={addrForm.label} onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })} /></div>
            <div><Label>Full name *</Label><Input value={addrForm.name} onChange={(e) => setAddrForm({ ...addrForm, name: e.target.value })} data-testid="profile-addr-name" /></div>
            <div className="col-span-2"><Label>Phone *</Label><Input value={addrForm.phone} onChange={(e) => setAddrForm({ ...addrForm, phone: e.target.value })} placeholder="+91…" /></div>
            <div className="col-span-2"><Label>Address line 1 *</Label><Input value={addrForm.line1} onChange={(e) => setAddrForm({ ...addrForm, line1: e.target.value })} /></div>
            <div className="col-span-2"><Label>Landmark</Label><Input value={addrForm.landmark} onChange={(e) => setAddrForm({ ...addrForm, landmark: e.target.value })} /></div>
            <div><Label>City *</Label><Input value={addrForm.city} onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })} /></div>
            <div><Label>State *</Label><Input value={addrForm.state} onChange={(e) => setAddrForm({ ...addrForm, state: e.target.value })} /></div>
            <div><Label>Pincode *</Label><Input value={addrForm.pincode} onChange={(e) => setAddrForm({ ...addrForm, pincode: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddrDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={addrBusy || !addrForm.name || !addrForm.phone || !addrForm.line1 || !addrForm.city || !addrForm.state || !addrForm.pincode} className="btn-primary-glow" data-testid="profile-save-addr">
              {addrBusy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
